import { dropMintTotals, itemMintState } from "./itemMint";
import type { Drop } from "./types";
import { fromItems } from "./v2/itemFacts";

/**
 * A DROP'S WHOLE-DROP FIGURES — supply, minted, sold out, price.
 *
 * The items now arrive a page at a time, so adding them up describes only the
 * page. The backend's `summary` covers every item (quantity, available, price
 * min/max, currency). Without it the items' own sums are used ONLY when the
 * page holds all of them (no second page); otherwise the figure stays unknown
 * rather than half-counted.
 *
 * WHEN THE PAGE HOLDS EVERY ITEM AND EACH STATES ITS QUANTITY AND NFTS, the
 * items decide supply, minted and sold out — the same count every item card
 * shows. The summary lagged behind them: an item added to a drop whose four
 * items were all minted was counted in `quantity` (5) but not in `available`
 * (0), so the drop read «5 / 5 minted · sold out» above an item with «Minted
 * 0 of 1» and a mint button. On a partial page the summary still speaks for
 * the drop, but it may never say fewer copies are left than the loaded items
 * show — a drop is not sold out while an item on screen still has copies.
 */
export function dropFigures(drop: Drop) {
  const items = drop.items ?? [];
  const all = !drop.itemsPage || drop.itemsPage.last <= 1;
  const s = drop.summary ?? null;
  const fromPage = all ? fromItems(items) : { supply: null, range: null, lowest: null };
  const totals = all ? dropMintTotals(items) : { minted: null, soldOut: false };

  const states = items.map(itemMintState);
  // Every item on the page states its quantity and its NFTs — then the page's own count is exact.
  const itemsKnow = all && totals.minted !== null && states.length > 0;
  // Copies the loaded items still have: the least the whole drop can have left.
  const leftOnPage = states.reduce((a, st) => a + (st.remaining ?? 0), 0);
  const available = s ? Math.max(s.available, Math.min(leftOnPage, s.quantity)) : null;

  const supply = itemsKnow
    ? states.reduce((a, st) => a + (st.quantity as number), 0)
    : s ? (s.quantity > 0 ? s.quantity : null) : fromPage.supply;
  const minted = itemsKnow
    ? totals.minted
    : s && available !== null
      ? Math.min(drop.mintedCount ?? s.quantity - available, s.quantity - available)
      : drop.mintedCount ?? totals.minted;
  const soldOut = itemsKnow ? totals.soldOut : s && available !== null ? s.quantity > 0 && available === 0 : totals.soldOut;
  const range = s && s.priceMin !== null && s.priceMax !== null && s.currencySymbol
    ? { min: s.priceMin, max: s.priceMax, symbol: s.currencySymbol }
    : fromPage.range;

  const itemsCount = drop.itemsCount ?? drop.itemsPage?.total ?? items.length;
  return {
    itemsCount,
    /** Known to hold no item at all (the count, or a summary of nothing) — open in time, but nothing to mint. */
    empty: items.length === 0 && (drop.itemsCount === 0 || drop.itemsPage?.total === 0 || (s !== null && s.quantity === 0)),
    supply,
    minted,
    soldOut,
    range,
    // Per-currency lowest prices need every item; the summary states one currency.
    lowest: range ? null : fromPage.lowest,
  };
}
